import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { eventApi } from '../../../core/api/rtk-query/event.api';
import { IProblemAttempts } from '../../../core/types/problem.interface';
import { Latex } from '../../common/main/latex/Latex';

const Wrap = styled.div`display:grid; gap:16px;`;
const Toolbar = styled.div`display:flex; gap:8px; align-items:center; flex-wrap:wrap; justify-content:space-between;`;
const Input = styled.input`padding:10px 12px; border-radius:10px; border:1px solid #dbe3f6; background:#fff; color:#0b1221; width:100%; box-sizing:border-box;`;
const Btn = styled.button`background:#fff; border:1px solid #dbe3f6; color:#0b1221; padding:8px 12px; border-radius:10px; cursor:pointer;`;
const Table = styled.table`
  width:100%; border-collapse:separate; border-spacing:0; background:#fff; border:1px solid #e5e8f0; border-radius:12px; overflow:hidden;
  thead th.active{ background:#eaf2ff !important; color:#0b68fe !important; }
`;
const Th = styled.th`text-align:left; padding:12px; background:#f7f9ff; border-bottom:1px solid #e9eefb; font-weight:600; color:#1b2b5a; position:sticky; top:0;`;
const Td = styled.td`padding:12px; border-bottom:1px solid #f1f4ff; color:#0b1221;`;
const PageBtn = styled.button`
  background:#fff; border:1px solid #dbe3f6; color:#0b1221; padding:8px 10px; border-radius:10px; min-width:36px; cursor:pointer;
  &[disabled]{opacity:.6; cursor:default}
`;
const Pagination = styled.div`display:flex; gap:6px; justify-content:flex-end; padding-top:10px;`;

const ModalBg = styled.div`position:fixed; inset:0; background:rgba(13,25,41,.36); display:flex; align-items:center; justify-content:center; z-index:1000;`;
const Modal = styled.div`width:900px; max-width:calc(100% - 24px); background:#fff; border-radius:16px; border:1px solid #e5e8f0; box-shadow:0 16px 48px rgba(0,0,0,.08); padding:16px; display:grid; gap:12px; max-height:90vh; overflow:hidden;`;
const ScrollArea = styled.div`overflow:auto; max-height:70vh; display:grid; gap:10px; padding-right:27px;`;
const TaskHeader = styled.button`
  all: unset; display:flex; width:100%; justify-content:space-between; align-items:center;
  padding:10px 12px; border:1px solid #dbe6ff; border-radius:10px; background:#eef3ff; cursor:pointer; font-weight:700;
`;
const TaskBlock = styled.div`display:grid; gap:8px;`;

function DeleteBtn({ eventId, userId, onDeleted }: { eventId: string; userId: string; onDeleted: () => void }) {
  const [deleteAttempt, { isLoading }] = eventApi.useDeleteUserEventMutation();
  const handleClick = async () => {
    const ok = window.confirm('Удалить попытку? Действие необратимо.');
    if (!ok) return;
    try {
      await deleteAttempt({ eventId, userId }).unwrap();
      onDeleted();
    } catch (err) {
      console.error('Ошибка удаления:', err);
    }
  };
  return <Btn onClick={handleClick} disabled={isLoading}>Удалить</Btn>;
}

function AttemptModal({ open, attempt, onClose, onDeleted }: { open: boolean; attempt: any | null; onClose: () => void; onDeleted: () => void }) {
  const skip = !open || !attempt;
  const { data } = eventApi.useGetUserEventByIdsQuery(
    { eventId: attempt?.eventId ?? '', userId: attempt?.userId ?? '' },
    { skip }
  );
  const [deleteAttempt, { isLoading: isDeleting }] = eventApi.useDeleteUserEventMutation();
  const [reopen] = eventApi.useReopenUserEventMutation();
  const [finish] = eventApi.useFinishUserEventForUserMutation();
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [attemptView, setAttemptView] = useState<any | null>(null);

  useEffect(() => {
    if (open && attempt) {
      setAttemptView(attempt);
      setExpandedIdx(null);
    }
  }, [open, attempt]);

  if (!open || !attempt) return null;

  const view = attemptView ?? attempt;

  const totalMax = view?.totalMaxPoints ?? view?.problemScores?.reduce((s: number, p: any) => s + p.maximumScore, 0) ?? 0;
  const totalUser = (data?.problemAttempts?.reduce((s: number, p: IProblemAttempts) => s + (p.points || 0), 0))
    ?? view?.totalUserPoints
    ?? view?.problemScores?.reduce((s: number, p: any) => s + p.userScore, 0)
    ?? 0;

  const handleDelete = async () => {
    if (!view) return;
    const ok = window.confirm('Удалить попытку? Действие необратимо.');
    if (!ok) return;
    await deleteAttempt({ eventId: view.eventId, userId: view.userId }).unwrap();
    onDeleted();
    onClose();
  };
  return (
    <ModalBg onClick={onClose}>
      <Modal onClick={e=> e.stopPropagation()}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <div style={{fontWeight:700, display:'flex', alignItems:'center', gap:10}}>
            Попытка • {view?.eventName}
            <button onClick={handleDelete} disabled={isDeleting} style={{all:'unset', cursor:'pointer', color:'#c1121f', fontSize:13}}>удалить</button>
          </div>
          <button onClick={onClose} style={{all:'unset', cursor:'pointer', padding:'4px 8px', fontSize:18}}>×</button>
        </div>
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
          <div>
            <div style={{color:'#667', marginBottom:6}}>Статус</div>
            <div style={{display:'flex', alignItems:'center', gap:8}}>
              <span>{view?.isManuallyFinished ? 'closed' : 'submitted'}</span>
              <Btn onClick={async()=>{
                if (!view) return;
                try {
                  if (view.isManuallyFinished) {
                    await reopen({ eventId: view.eventId, userId: view.userId }).unwrap();
                    setAttemptView({ ...view, isManuallyFinished: false });
                  } else {
                    await finish({ eventId: view.eventId, userId: view.userId }).unwrap();
                    setAttemptView({ ...view, isManuallyFinished: true });
                  }
                  onDeleted();
                } catch {}
              }}>{view?.isManuallyFinished ? 'Открыть' : 'Закрыть'}</Btn>
            </div>
          </div>
          <div>
            <div style={{color:'#667', marginBottom:6}}>Баллы</div>
            <div>{totalUser}/{totalMax}</div>
          </div>
        </div>
        <div>
          <div style={{color:'#667', marginBottom:6}}>Условия и ответы по задачам</div>
          <ScrollArea>
            {(data?.problemAttempts?.map((pa: IProblemAttempts, idx: number) => (
              <TaskBlock key={idx}>
                <TaskHeader onClick={()=> setExpandedIdx(expandedIdx === idx ? null : idx)}>
                  <div>Задача #{idx+1} • Баллы: {pa.points ?? 0}</div>
                  <div style={{color:'#556'}}>{expandedIdx === idx ? 'Свернуть' : 'Показать'}</div>
                </TaskHeader>
                {expandedIdx === idx && (
                  <div style={{display:'grid', gap:8}}>
                    <div style={{color:'#667'}}>Условие</div>
                    <div style={{background:'#fff', padding:0}}>
                      <Latex content={pa.condition || ''} />
                    </div>
                    <div style={{color:'#667'}}>Ответ пользователя</div>
                    <div style={{whiteSpace:'pre-wrap', borderRadius:10, padding:12,
                      background: (pa.points ?? 0) > 0 ? '#e9f7ef' : '#fdecea',
                      color: (pa.points ?? 0) > 0 ? '#0f5132' : '#611a15'
                    }}>
                      {pa.sentMessage || '—'} {(pa.points ?? 0) > 0 ? '(верно)' : '(неверно)'}
                    </div>
                  </div>
                )}
              </TaskBlock>
            ))) || <div style={{color:'#667'}}>Данные по задачам отсутствуют</div>}
          </ScrollArea>
        </div>
        <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}></div>
      </Modal>
    </ModalBg>
  );
}

export const AdminAttemptsPage = () => {
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<'date'|'userName'|'eventName'|'status'|'totalUserPoints'>('date');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');
  // фильтры
  const [fDate, setFDate] = useState('');
  const [fUser, setFUser] = useState('');
  const [fEvent, setFEvent] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fScoreMin, setFScoreMin] = useState('');
  const [fScoreMax, setFScoreMax] = useState('');
  const [evSuggest, setEvSuggest] = useState<string>('');
  const { data: evOptions } = eventApi.useSearchEventsQuery({ q: evSuggest || fEvent, limit: 10 });
  const sortKeyParam = sortKey.toLowerCase();
  const { data: searchRes, refetch } = eventApi.useSearchAttemptsQuery({
    user: fUser || undefined,
    ev: fEvent || undefined,
    date: fDate || undefined,
    status: fStatus || undefined,
    scoreMin: fScoreMin ? Number(fScoreMin) : undefined,
    scoreMax: fScoreMax ? Number(fScoreMax) : undefined,
    page,
    size: 50,
    sortBy: sortKeyParam,
    sortDir,
  });
  const [exportSearch] = eventApi.useLazySearchAttemptsQuery();

  const [selected, setSelected] = useState<any | null>(null);

  const rows = useMemo(()=>{
    // Только маппинг (вся фильтрация/сортировка выполнена на сервере)
    const list = (searchRes?.items || []).map((it:any) => ({
      date: it.startTime as string,
      userName: it.userName as string,
      eventName: it.eventName as string,
      status: it.isManuallyFinished ? 'closed' : 'submitted',
      totalUserPoints: Number(it.totalUserPoints || 0),
      totalMaxPoints: Number(it.totalMaxPoints || 0),
      raw: it,
    }));
    return list;
  }, [searchRes]);

  return (
    <Wrap>
      <Toolbar>
        <h2 style={{margin:0}}>Попытки</h2>
        <Btn onClick={async()=>{
          const total = searchRes?.number || 0;
          const size = Math.max(1000, total || 1000);
          const res = await exportSearch({
            user: fUser || undefined,
            ev: fEvent || undefined,
            date: fDate || undefined,
            status: fStatus || undefined,
            scoreMin: fScoreMin ? Number(fScoreMin) : undefined,
            scoreMax: fScoreMax ? Number(fScoreMax) : undefined,
            page: 1,
            size,
            sortBy: sortKey,
            sortDir,
          }).unwrap();
          const all = res.items || [];
          const headers = ['date','userName','eventName','status','totalUserPoints','totalMaxPoints'];
          const mapped = all.map((it:any)=>({
            date: (it.startTime||'').slice(0,19).replace('T',' '),
            userName: it.userName,
            eventName: it.eventName,
            status: it.isManuallyFinished ? 'closed' : 'submitted',
            totalUserPoints: it.totalUserPoints ?? 0,
            totalMaxPoints: it.totalMaxPoints ?? 0,
          }));
          const csv = [headers.join(';'), ...mapped.map((r:any) => headers.map(h => String((r as any)[h] ?? '')).join(';'))].join('\n');
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = 'attempts.csv'; a.click(); URL.revokeObjectURL(url);
        }}>Экспорт CSV</Btn>
      </Toolbar>

      <Table>
        <thead>
          <tr>
            <Th className={sortKey==='date'?'active':''} onClick={()=>{ setSortKey('date'); setSortDir(d=> d==='asc'?'desc':'asc'); setPage(1); }} style={{cursor:'pointer'}}>Дата {sortKey==='date' && (sortDir==='asc'?'▲':'▼')}</Th>
            <Th className={sortKey==='userName'?'active':''} onClick={()=>{ setSortKey('userName'); setSortDir(d=> d==='asc'?'desc':'asc'); setPage(1); }} style={{cursor:'pointer'}}>Пользователь {sortKey==='userName' && (sortDir==='asc'?'▲':'▼')}</Th>
            <Th className={sortKey==='eventName'?'active':''} onClick={()=>{ setSortKey('eventName'); setSortDir(d=> d==='asc'?'desc':'asc'); setPage(1); }} style={{cursor:'pointer'}}>Мероприятие {sortKey==='eventName' && (sortDir==='asc'?'▲':'▼')}</Th>
            <Th className={sortKey==='status'?'active':''} onClick={()=>{ setSortKey('status'); setSortDir(d=> d==='asc'?'desc':'asc'); setPage(1); }} style={{cursor:'pointer'}}>Статус {sortKey==='status' && (sortDir==='asc'?'▲':'▼')}</Th>
            <Th className={sortKey==='totalUserPoints'?'active':''} onClick={()=>{ setSortKey('totalUserPoints'); setSortDir(d=> d==='asc'?'desc':'asc'); setPage(1); }} style={{cursor:'pointer'}}>Баллы {sortKey==='totalUserPoints' && (sortDir==='asc'?'▲':'▼')}</Th>
            <Th>Действия</Th>
          </tr>
          <tr>
            <Th style={{position:'relative'}}>
              <Input placeholder="Дата…" value={fDate} onChange={e=> setFDate(e.target.value)} />
              {fDate && (<span onClick={()=> setFDate('')} style={{position:'absolute', right:8, top:8, cursor:'pointer', color:'#98a1b3'}}>×</span>)}
            </Th>
            <Th style={{position:'relative'}}>
              <Input placeholder="Пользователь…" value={fUser} onChange={e=> setFUser(e.target.value)} />
              {fUser && (<span onClick={()=> setFUser('')} style={{position:'absolute', right:8, top:8, cursor:'pointer', color:'#98a1b3'}}>×</span>)}
            </Th>
            <Th style={{position:'relative'}}>
              <Input placeholder="Мероприятие…" value={fEvent} onChange={e=> { setFEvent(e.target.value); setEvSuggest(e.target.value); }} onFocus={()=> setEvSuggest(fEvent)} />
              {!!evOptions?.length && (evSuggest||'').length>0 && (
                <div style={{position:'absolute', zIndex:5, background:'#fff', border:'1px solid #e5e8f0', borderRadius:10, marginTop:4, width:'100%'}}>
                  {evOptions.map((o:any)=>(
                    <div key={o.id} style={{padding:'8px 10px', cursor:'pointer'}} onMouseDown={()=>{ setFEvent(o.name); setEvSuggest(''); }}>
                      {o.name}
                    </div>
                  ))}
                </div>
              )}
              {fEvent && (<span onClick={()=> setFEvent('')} style={{position:'absolute', right:8, top:8, cursor:'pointer', color:'#98a1b3'}}>×</span>)}
            </Th>
            <Th>
              <select value={fStatus} onChange={e=> setFStatus(e.target.value)} style={{padding:'10px 12px', borderRadius:10, border:'1px solid #dbe3f6'}}>
                <option value="">—</option>
                <option value="submitted">submitted</option>
                <option value="closed">closed</option>
              </select>
            </Th>
            <Th>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:6}}>
                <Input placeholder=">= мин" value={fScoreMin} onChange={e=> setFScoreMin(e.target.value)} />
                <Input placeholder="<= макс" value={fScoreMax} onChange={e=> setFScoreMax(e.target.value)} />
              </div>
            </Th>
            <Th></Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx)=> (
            <tr key={idx}>
              <Td style={{textAlign:'left'}}><span style={{cursor:'pointer'}} onClick={()=> setFDate((r.date||'').slice(0,10))}>{(r.date||'').slice(0,19).replace('T',' ')}</span></Td>
              <Td style={{textAlign:'left'}}><span style={{cursor:'pointer'}} onClick={()=> setFUser(r.userName || '')}>{r.userName || '—'}</span></Td>
              <Td style={{textAlign:'center'}}><span style={{cursor:'pointer'}} onClick={()=> setFEvent(r.eventName || '')}>{r.eventName}</span></Td>
              <Td>{r.status}</Td>
              <Td>{r.totalUserPoints}/{r.totalMaxPoints}</Td>
              <Td>
                <div style={{display:'flex', gap:8}}>
                  <Btn onClick={()=> setSelected({
                    eventId: r.raw.eventId ?? r.raw.EventId,
                    userId: r.raw.userId ?? r.raw.UserId,
                    eventName: r.raw.eventName ?? r.raw.EventName,
                    isManuallyFinished: r.raw.isManuallyFinished ?? r.raw.IsManuallyFinished,
                    totalUserPoints: r.raw.totalUserPoints ?? r.raw.TotalUserPoints,
                    totalMaxPoints: r.raw.totalMaxPoints ?? r.raw.TotalMaxPoints,
                  })}>Детали</Btn>
                  <DeleteBtn eventId={r.raw.eventId ?? r.raw.EventId} userId={r.raw.userId ?? r.raw.UserId} onDeleted={refetch} />
                </div>
              </Td>
            </tr>
          ))}
          {!rows.length && (
            <tr><Td colSpan={6} style={{color:'#667'}}>Ничего не найдено</Td></tr>
          )}
        </tbody>
      </Table>
      <Pagination>
        {(() => {
          const total = searchRes?.number || 0;
          const pageSize = 50;
          const pages = Math.max(1, Math.ceil(total / pageSize));
          const cur = Math.min(page, pages);
          const makeBtn = (p:number, label?:string, disabled?:boolean) => (
            <PageBtn key={`p-${label||p}`} disabled={disabled||p===cur} onClick={()=> setPage(p)}>{label||p}</PageBtn>
          );
          const items: any[] = [];
          if (pages <= 9) {
            for (let i=1; i<=pages; i++) items.push(makeBtn(i));
          } else {
            // 1 2 … (cur-1) cur (cur+1) … (pages-1) pages
            items.push(makeBtn(1));
            items.push(makeBtn(2));
            if (cur > 4) items.push(makeBtn(cur-1, '…', true));
            const from = Math.max(3, cur-1);
            const to = Math.min(pages-2, cur+1);
            for (let i=from; i<=to; i++) items.push(makeBtn(i));
            if (cur < pages-3) items.push(makeBtn(cur+1, '…', true));
            items.push(makeBtn(pages-1));
            items.push(makeBtn(pages));
          }
          return items;
        })()}
      </Pagination>

      <AttemptModal open={!!selected} attempt={selected} onClose={()=> setSelected(null)} onDeleted={()=> refetch()} />
    </Wrap>
  );
};


